import { format } from "date-fns";
import { useI18n } from "hooks/i18n/i18n";
import { PAYMENT_STATUS } from "pages/AccountSettlementHistoryOrderId/constants/PaymentStatus";
import { useMemo } from "react";
import { getPaymentType } from "utility/getPaymentType";

import type { OrderData } from "../../types/OrderData";
import type { PaymentStatus } from "@spwn/types/spwn";
import type { Store } from "store";

type UseOrderDataInput = {
  transaction?: Store["purchase"]["activeTransactionMap"][string];
};

type UseOrderDataOutput = {
  orderData: OrderData | null;
};

type PaymentStatuses = typeof PAYMENT_STATUS;
const paymentStatus: Record<string, PaymentStatuses[keyof PaymentStatuses]> =
  PAYMENT_STATUS;

const getPaymentStatus = (status: string | null): PaymentStatus => {
  if (status === null) return PAYMENT_STATUS.UNPROCESS;
  return paymentStatus[status] ?? PAYMENT_STATUS.UNPROCESS;
};

const getPurchasedAt = (seconds: number, language: string): string => {
  const date = new Date(seconds * 1000);
  return language === "ja"
    ? format(date, "yyyy年MM月dd日")
    : format(date, "yyyy/MM/dd");
};

const getPurchasedCount = (productCountMap: Record<string, number>): number => {
  return Object.values(productCountMap).reduce(
    (acc, curr) => acc + (curr ?? 0),
    0
  );
};

export const useOrderData = (input: UseOrderDataInput): UseOrderDataOutput => {
  const { transaction } = input;
  const { i18n } = useI18n();

  const orderData: OrderData | null = useMemo(() => {
    if (!transaction) return null;

    return {
      orderId: transaction.orderId ?? 0,
      purchasedAt: getPurchasedAt(
        transaction.createdAt?.seconds ?? 0,
        i18n.language
      ),
      purchasedCount: getPurchasedCount(transaction.productCountMap ?? {}),
      purchasedType: getPaymentType(transaction.payType),
      paymentStatus: getPaymentStatus(transaction.status),
      totalPrice: transaction.totalPrice,
      shippingFee: transaction.shippingFee ?? 0,
      serviceFee: transaction.serviceFee ?? 0,
      settlementFee: transaction.settlementFee ?? 0,
    };
  }, [transaction, i18n.language]);

  return { orderData };
};
